import React, {useEffect, useState} from 'react';
import {
  Grid,
  Typography,
  CircularProgress,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import useReduxState from '../../core/useReduxState';
import api from '../../core/api';
import ReturnButton from '../ReturnButton';
import {Formik} from 'formik';
import ButtonSubmit from './ButtonSubmit';
import * as Yup from 'yup';
import {formatDate} from '../../utils/formatDate';
import Business from '../../models/Business';
import Confirm from '../Confirm';

export const createValidationSchema = couponData => {
  const originalEndDate = couponData?.endDate;
  const couponStatus = couponData?.status;

  return Yup.object().shape({
    couponCode: Yup.string().required('Código do Cupom é obrigatório'),
    discountType: Yup.string().required('Tipo de Desconto é obrigatório'),
    value: Yup.number()
      .typeError('Valor deve ser um número')
      .required('Valor é obrigatório')
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' ? undefined : value,
      )
      .when('discountType', {
        is: 'percentage',
        then: schema => {
          return schema.max(100, 'A porcentagem não pode ser maior que 100');
        },
      }),
    startDate: Yup.string().required('Data de Início é obrigatória'),
    endDate: Yup.string()
      .required('Data Final é obrigatória')
      .test(
        'is-not-reduced',
        'A nova Data Final não pode ser anterior à data anteriormente salva.',
        function(value) {
          if (couponStatus !== 'active') {
            return true;
          }

          if (!originalEndDate || !value) {
            return true;
          }

          const novaData = new Date(value);
          const dataOriginal = new Date(originalEndDate);

          novaData.setHours(23, 59, 59, 999);
          dataOriginal.setHours(23, 59, 59, 999);

          return novaData >= dataOriginal;
        },
      )
      .test(
        'is-after-startDate',
        'A Data Final não pode ser anterior à Data de Início.',
        function(value) {
          const {startDate} = this.parent;

          if (!value || !startDate) return true;

          const start = new Date(startDate.split('T')[0]);
          const end = new Date(value);

          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);

          const startTime = start.getTime();
          const endTime = end.getTime();

          return endTime > startTime;
        },
      )
      .test(
        'is-after-startDate',
        'A Data Final não pode ser anterior à Data de Hoje.',
        function(value) {
          if (!value) return true;

          const now = new Date();
          const end = new Date(value);

          now.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);

          const startTime = now.getTime();
          const endTime = end.getTime();

          return endTime > startTime;
        },
      ),
    validationType: Yup.string().required('Tipo de validade é obrigatório'),

    expirationDate: Yup.string()
      .nullable()
      .when('validationType', {
        is: 'byDate',
        then: schema =>
          schema
            .required('Data de Expiração é obrigatória')
            .test(
              'is-after-endDate',
              'A data de expiração não pode ser anterior à data final',
              function(value) {
                const {endDate} = this.parent;
                if (!value || !endDate) return true;
                const exp = new Date(value);
                const end = new Date(endDate.split('T')[0]);
                exp.setHours(23, 59, 59, 999);
                end.setHours(23, 59, 59, 999);
                return exp >= end;
              },
            ),
        otherwise: schema => schema.notRequired(),
      }),

    numberRenewalLimits: Yup.number()
      .nullable()
      .when('validationType', {
        is: 'byPayment',
        then: schema =>
          schema
            .typeError('Valor deve ser um número')
            .required('Número limite de renovações é obrigatório')
            .transform((value, originalValue) =>
              String(originalValue).trim() === '' ? undefined : Number(value),
            ),
        otherwise: schema => schema.notRequired(),
      }),
    usageLimit: Yup.number()
      .typeError('Limite de Uso deve ser um número')
      .required('Limite de Uso é obrigatório')
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' ? undefined : value,
      ),
    status: Yup.string().default('inactive'),
    eligibleUsers: Yup.string().required('Usuários Elegíveis é obrigatório'),
    eligiblePlans: Yup.string().required('Planos Elegíveis é obrigatório'),
    description: Yup.string(),
    editable: Yup.boolean().default(true),
  });
};

const CouponForm = ({coupon, history}) => {
  const editing = !!coupon.id;
  const [, setErrors] = useState({});
  const [errorValidate, setErrorValidate] = useState(null);

  const [getUser, setUser] = useReduxState({
    loading: false,
  });
  const {enqueueSnackbar} = useSnackbar();

  const [allBusinesses, setBusinesses] = useState([]);
  const fetchEmpresa = async () => {
    try {
      const getAllEmpresas = await Business.findAll();
      setBusinesses(getAllEmpresas);
    } catch (e) {
      // console.log({e});
    }
  };
  useEffect(() => {
    fetchEmpresa();
  }, []);

  const onSubmit = async form => {
    setErrorValidate(null);
    const payload = {
      couponCode: form.couponCode,
      discountType: form.discountType,
      value: form.value,
      usageLimit: form.usageLimit,
      startDate: form.startDate,
      endDate: form.endDate,
      validationType: form.validationType,
      ...(form.validationType === 'byDate'
        ? {expirationDate: form.expirationDate, numberRenewalLimits: null}
        : {
            expirationDate: null,
            numberRenewalLimits: form.numberRenewalLimits,
          }),
      status: form.status,
      eligibleUsers: form.eligibleUsers,
      eligiblePlans: form.eligiblePlans,
      description: form.description,
      editable: form.status === 'active' ? false : form.editable,
      businessIds:
        form?.businesses?.length > 0 ? form.businesses.map(b => b.id) : [],
    };

    try {
      const {data: plans} = await api.get(`/plans`);

      const planM = plans[0];
      const planY = plans[1];

      const discountedM =
        payload.discountType === 'fixed'
          ? planM.value - payload.value
          : planM.value - (planM.value * payload.value) / 100;

      const discountedY =
        payload.discountType === 'fixed'
          ? planY.value - payload.value
          : planY.value - (planY.value * payload.value) / 100;

      const applyDiscountError = (planType, finalValue) => {
        let errorMessage = '';

        if (finalValue < 5) {
          errorMessage = `O valor final do plano ${planType} (R$ ${finalValue.toFixed(
            2,
          )}) é muito baixo. O mínimo deve ser R$ 5,00.`;
        }

        if (errorMessage) {
          console.error('ERRO DE DESCONTO:', errorMessage);
        }

        setErrorValidate(errorMessage);
        return !!errorMessage;
      };

      let hasError = false;

      if (payload.eligiblePlans === 'all' || payload.eligiblePlans === 'M') {
        if (discountedM < 5) {
          hasError = applyDiscountError('Mensal', discountedM);
        }
      }

      if (
        !hasError &&
        (payload.eligiblePlans === 'all' || payload.eligiblePlans === 'Y')
      ) {
        if (discountedY < 5) {
          applyDiscountError('Anual', discountedY);
          hasError = true;
        }
      }

      if (!hasError) {
        if (editing) {
          if (payload.status === 'active') {
            const res = await Confirm(
              'Ao Ativar e Salvar Cupom, ele se tornará disponível para resgate e não poderá mais ser editado. Deseja continuar?',
            );
            if (res) {
              setUser({loading: true});

              await api.put(`/coupons/${form.id}`, payload).then(res => {
                if (res.status === 200) {
                  enqueueSnackbar('Dados alterados com sucesso!');
                  history.push('/cupons');
                }
              });
            }
            return;
          } else {
            setUser({loading: true});

            await api.put(`/coupons/${form.id}`, payload).then(res => {
              if (res.status === 200) {
                enqueueSnackbar('Dados alterados com sucesso!');
                history.push('/cupons');
              }
            });
          }
        } else {
          await api.post(`/coupons`, payload).then(res => {
            // console.log(res);
            if (res.status === 200) {
              enqueueSnackbar('Novo cupom cadastrado com sucesso!');
              history.push('/cupons');
            }
          });
        }
      }
    } catch ({response}) {
      enqueueSnackbar(
        response.data.message ||
          'Ocorreu um erro ao cadastrar cupom. Tente novamente.',
      );
      setUser({loading: false});
    } finally {
      setUser({loading: false});
    }
  };

  const CouponFormEffectHandler = ({formik}) => {
    useEffect(() => {
      const error = formik.errors;
      const hasRegisterError =
        !!error.couponCode ||
        !!error.discountType ||
        !!error.value ||
        !!error.numberRenewalLimits ||
        !!error.startDate ||
        !!error.endDate ||
        !!error.validationType ||
        !!error.expirationDate ||
        !!error.usageLimit ||
        !!error.status ||
        !!error.eligibleUsers ||
        !!error.eligiblePlans ||
        !!error.description ||
        !!error.editable;

      setErrors(prev => {
        if (prev.register !== hasRegisterError) {
          return {
            ...prev,
            register: hasRegisterError,
          };
        }
        return prev;
      });
    }, [formik.errors]);

    return null;
  };

  return (
    <Formik
      initialValues={coupon}
      validationSchema={createValidationSchema(coupon)}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={values => {
        onSubmit(values);
      }}>
      {formikProps => {
        return (
          <>
            <CouponFormEffectHandler formik={formikProps} />
            <Grid container xs={12}>
              <Grid item xs={12} alignContent="center">
                <Typography component="h1" variant="h5">
                  <ReturnButton />
                  {editing ? 'Editar Cupom' : 'Cadastrar Novo Cupom'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={12} lg={12} sx={{gap: 1}}>
                {!getUser().loading ? (
                  <form
                    onSubmit={formikProps.handleSubmit}
                    style={{marginTop: '20px'}}>
                    <Grid container spacing={2}>
                      {editing && (
                        <Grid item xs={12} sm={12} md={12}>
                          <Grid item xs={12} sm={4} md={4}>
                            <TextField
                              select
                              fullWidth
                              variant="outlined"
                              label="Status *"
                              name="status"
                              value={formikProps.values.status || '-'}
                              onChange={formikProps.handleChange}
                              helperText={
                                formikProps.touched.status &&
                                formikProps.errors.status
                              }
                              error={
                                formikProps.touched.status &&
                                Boolean(formikProps.errors.status)
                              }>
                              <MenuItem value="active">Ativo</MenuItem>
                              <MenuItem value="inactive">Inativo</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={9} md={9}>
                        <TextField
                          fullWidth
                          margin="none"
                          variant="outlined"
                          id="couponCode"
                          name="couponCode"
                          label="Código do Cupom *"
                          disabled={!coupon.editable}
                          value={formikProps.values.couponCode}
                          onChange={e => {
                            const newValue = e.target.value
                              .replace(/\s/g, '')
                              .toUpperCase();
                            if (newValue.length <= 20) {
                              formikProps.setFieldValue('couponCode', newValue);
                            }
                          }}
                          helperText={
                            formikProps.touched.couponCode &&
                            formikProps.errors.couponCode
                          }
                          error={
                            formikProps.touched.couponCode &&
                            Boolean(formikProps.errors.couponCode)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} md={3}>
                        <TextField
                          fullWidth
                          margin="none"
                          variant="outlined"
                          id="usageLimit"
                          name="usageLimit"
                          label="Limite de Uso *"
                          disabled={!coupon.editable}
                          value={formikProps.values.usageLimit}
                          onChange={e => {
                            const onlyNumbers = e.target.value.replace(
                              /\D/g,
                              '',
                            );
                            formikProps.setFieldValue(
                              'usageLimit',
                              onlyNumbers,
                            );
                          }}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                          }}
                          helperText={
                            formikProps.touched.usageLimit &&
                            formikProps.errors.usageLimit
                          }
                          error={
                            formikProps.touched.usageLimit &&
                            Boolean(formikProps.errors.usageLimit)
                          }
                        />
                      </Grid>

                      <Grid item xs={6} sm={3} md={3}>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          label="Tipo de Desconto *"
                          id="discountType"
                          name="discountType"
                          disabled={!coupon.editable}
                          value={formikProps.values.discountType || '-'}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.discountType &&
                            formikProps.errors.discountType
                          }
                          error={
                            formikProps.touched.discountType &&
                            Boolean(formikProps.errors.discountType)
                          }>
                          <MenuItem value="percentage">Porcentagem</MenuItem>
                          <MenuItem value="fixed">Fixo</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={6} sm={3} md={3}>
                        <TextField
                          fullWidth
                          margin="none"
                          variant="outlined"
                          id="value"
                          name="value"
                          type="number"
                          label="Valor *"
                          disabled={!coupon.editable}
                          value={formikProps.values.value}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.value &&
                            formikProps.errors.value
                          }
                          error={
                            formikProps.touched.value &&
                            Boolean(formikProps.errors.value)
                          }
                        />
                      </Grid>

                      <Grid item xs={6} sm={3} md={3}>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          label="Tipo de Validação *"
                          id="validationType"
                          name="validationType"
                          disabled={!coupon.editable}
                          value={formikProps.values.validationType || '-'}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.validationType &&
                            formikProps.errors.validationType
                          }
                          error={
                            formikProps.touched.validationType &&
                            Boolean(formikProps.errors.validationType)
                          }>
                          <MenuItem value="byDate">Por Data</MenuItem>
                          <MenuItem value="byPayment">Por Parcela</MenuItem>
                        </TextField>
                      </Grid>

                      {formikProps.values.validationType === 'byDate' ? (
                        <Grid item xs={6} sm={3} md={3}>
                          <TextField
                            fullWidth
                            margin="none"
                            variant="outlined"
                            type="date"
                            id="expirationDate"
                            name="expirationDate"
                            label="Data de Expiração *"
                            disabled={!coupon.editable}
                            InputLabelProps={{shrink: true}}
                            value={formatDate(
                              formikProps.values.expirationDate,
                            )}
                            onChange={formikProps.handleChange}
                            helperText={
                              formikProps.touched.expirationDate &&
                              formikProps.errors.expirationDate
                            }
                            error={
                              formikProps.touched.expirationDate &&
                              Boolean(formikProps.errors.expirationDate)
                            }
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={6} sm={3} md={3}>
                          <TextField
                            fullWidth
                            margin="none"
                            variant="outlined"
                            id="numberRenewalLimits"
                            name="numberRenewalLimits"
                            label="Limite de Renovação *"
                            disabled={!coupon.editable}
                            value={formikProps.values.numberRenewalLimits}
                            onChange={e => {
                              const onlyNumbers = e.target.value.replace(
                                /\D/g,
                                '',
                              );
                              formikProps.setFieldValue(
                                'numberRenewalLimits',
                                onlyNumbers,
                              );
                            }}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                            }}
                            helperText={
                              formikProps.touched.numberRenewalLimits &&
                              formikProps.errors.numberRenewalLimits
                            }
                            error={
                              formikProps.touched.numberRenewalLimits &&
                              Boolean(formikProps.errors.numberRenewalLimits)
                            }
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} sm={3} md={3}>
                        <TextField
                          fullWidth
                          margin="none"
                          variant="outlined"
                          type="date"
                          id="startDate"
                          name="startDate"
                          label="Data de Início *"
                          InputLabelProps={{shrink: true}}
                          disabled={!coupon.editable}
                          value={formatDate(formikProps.values.startDate)}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.startDate &&
                            formikProps.errors.startDate
                          }
                          error={
                            formikProps.touched.startDate &&
                            Boolean(formikProps.errors.startDate)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} md={3}>
                        <TextField
                          fullWidth
                          margin="none"
                          variant="outlined"
                          type="date"
                          id="endDate"
                          name="endDate"
                          label="Data de Término *"
                          InputLabelProps={{shrink: true}}
                          value={formatDate(formikProps.values.endDate)}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.endDate &&
                            formikProps.errors.endDate
                          }
                          error={
                            formikProps.touched.endDate &&
                            Boolean(formikProps.errors.endDate)
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={3} md={3}>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          label="Usuários Elegíveis *"
                          name="eligibleUsers"
                          disabled={!coupon.editable}
                          value={formikProps.values.eligibleUsers || '-'}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.eligibleUsers &&
                            formikProps.errors.eligibleUsers
                          }
                          error={
                            formikProps.touched.eligibleUsers &&
                            Boolean(formikProps.errors.eligibleUsers)
                          }>
                          <MenuItem value="all">Todos</MenuItem>
                          <MenuItem value="news">Novos</MenuItem>
                          <MenuItem value="old">Antigos</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={3} md={3}>
                        <TextField
                          select
                          fullWidth
                          variant="outlined"
                          label="Planos Elegíveis *"
                          name="eligiblePlans"
                          disabled={!coupon.editable}
                          value={formikProps.values.eligiblePlans || '-'}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.eligiblePlans &&
                            formikProps.errors.eligiblePlans
                          }
                          error={
                            formikProps.touched.eligiblePlans &&
                            Boolean(formikProps.errors.eligiblePlans)
                          }>
                          <MenuItem value="M">Mensal</MenuItem>
                          <MenuItem value="Y">Anual</MenuItem>
                          <MenuItem value="all">Todos</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            id="businesses-label"
                            style={{
                              backgroundColor: 'white',
                              paddingLeft:
                                formikProps?.values?.businesses?.length > 0
                                  ? 5
                                  : 0,
                              paddingRight: 5,
                            }}>
                            Empresas vinculadas
                          </InputLabel>

                          <Select
                            labelId="businesses-label"
                            id="businesses"
                            name="businesses"
                            multiple
                            disabled={!coupon.editable}
                            value={
                              formikProps.values.businesses?.map(e => e.id) ||
                              []
                            }
                            onChange={e => {
                              const selectedIds = e.target.value;

                              const selectedBusinesses = allBusinesses.filter(
                                b => selectedIds.includes(b.id),
                              );

                              formikProps.setFieldValue(
                                'businesses',
                                selectedBusinesses,
                              );
                            }}
                            renderValue={selectedIds =>
                              allBusinesses
                                .filter(b => selectedIds.includes(b.id))
                                .map(b => b.corporateName)
                                .join(', ')
                            }>
                            {allBusinesses.map(business => (
                              <MenuItem key={business.id} value={business.id}>
                                <Checkbox
                                  checked={formikProps.values.businesses?.some(
                                    e => e.id === business.id,
                                  )}
                                />
                                <ListItemText
                                  primary={business.corporateName}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Lista das empresas selecionadas ordenadas */}
                        {formikProps.values.businesses &&
                          formikProps.values.businesses.length > 0 && (
                            <Grid item xs={12} sx={{mt: 2}}>
                              <ul>
                                {formikProps.values.businesses
                                  .slice()
                                  .sort((a, b) =>
                                    a.corporateName.localeCompare(
                                      b.corporateName,
                                    ),
                                  )
                                  .map(business => (
                                    <li key={business.id}>
                                      {business.corporateName}
                                    </li>
                                  ))}
                              </ul>
                            </Grid>
                          )}
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          margin="none"
                          variant="outlined"
                          id="description"
                          name="description"
                          label="Descrição *"
                          disabled={!coupon.editable}
                          value={formikProps.values.description}
                          onChange={formikProps.handleChange}
                          helperText={
                            formikProps.touched.description &&
                            formikProps.errors.description
                          }
                          error={
                            formikProps.touched.description &&
                            Boolean(formikProps.errors.description)
                          }
                        />
                      </Grid>
                    </Grid>

                    {errorValidate && (
                      <div
                        style={{
                          display: 'flex',
                          height: '100%',
                          justifyContent: 'flex-end',
                          flexDirection: 'column',
                          alignContent: 'end',
                        }}>
                        <p
                          style={{
                            textAlign: 'justify',
                            color: 'red',
                          }}>
                          {errorValidate}
                        </p>
                      </div>
                    )}

                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                      <ButtonSubmit
                        style={{
                          marginTop: 30,
                          width: '20vw',
                          alignSelf: 'center',
                        }}
                        type="submit"
                        variant="contained"
                        color="primary">
                        {editing ? 'Salvar' : 'Cadastrar'}
                      </ButtonSubmit>
                    </div>
                  </form>
                ) : (
                  <div
                    className="flex-col center-a center-b"
                    style={{height: '50vh'}}>
                    <CircularProgress size={100} />
                    <p> Carregando... </p>
                  </div>
                )}
              </Grid>
            </Grid>
          </>
        );
      }}
    </Formik>
  );
};

export default CouponForm;
